import { PageNavItem, PageConfig, CommonPageId, IconName } from "@mental-mint/shared-ui";
import { wordDriftIconLarge } from "@mental-mint/shared-ui";

/**
 * Page identifiers for WordDrift.
 * These represent both game-specific and shared common pages.
 */
export const WordDriftPageId = {
  Play: "word-drift-play",
  PostGame: "word-drift-post-game",
  PuzzleIntro: "word-drift-puzzle-intro",
} as const;

export type WordDriftPageId = typeof WordDriftPageId[keyof typeof WordDriftPageId];

/**
 * Navigation configuration for WordDrift pages.
 * Maps page IDs to their navigation bar display properties.
 */
export const wordDriftNavConfig: Record<string, PageNavItem> = {
  [WordDriftPageId.PuzzleIntro]: {
    label: "Help",
    icon: IconName.HelpCircle,
    order: 1,
  },
  [CommonPageId.Archive]: {
    label: "Archive",
    icon: IconName.Archive,
    order: 2,
  },
  [CommonPageId.More]: {
    label: "More",
    icon: IconName.MoreHorizontal,
    order: 3,
  }
};

/**
 * Page configuration for WordDrift.
 * Defines which pages show as modals vs full-screen and their nav bar presence.
 */
export const wordDriftPageConfig: Record<string, PageConfig> = {
  [WordDriftPageId.Play]: {
    id: WordDriftPageId.Play,
    navItem: null, // Play page does not appear in nav bar
  },
  [WordDriftPageId.PostGame]: {
    id: WordDriftPageId.PostGame,
    modalPageConfig: {
      humanReadableTitle: "",
      headerImage: wordDriftIconLarge,
    },
    navItem: null, // Post-game page does not appear in nav bar
  },
  [WordDriftPageId.PuzzleIntro]: {
    id: WordDriftPageId.PuzzleIntro,
    modalPageConfig: {
      // Title will be set dynamically in the PuzzleIntro page content
    },
    navItem: wordDriftNavConfig[WordDriftPageId.PuzzleIntro],
  },
  [CommonPageId.Archive]: {
    id: CommonPageId.Archive,
    modalPageConfig: {
      humanReadableTitle: "Puzzle Archive"
    },
    navItem: wordDriftNavConfig[CommonPageId.Archive],
  },
  [CommonPageId.More]: {
    id: CommonPageId.More,
    modalPageConfig: {
      humanReadableTitle: "More Games"
    },
    navItem: wordDriftNavConfig[CommonPageId.More],
  },
};

/**
 * Gets the navigation bar items for WordDrift in display order.
 * Filters out pages that shouldn't appear in the nav bar.
 */
export function getWordDriftNavItems(): PageNavItem[] {
  return Object.values(wordDriftPageConfig)
    .filter((config) => config.navItem !== null)
    .map((config) => config.navItem!)
    .sort((a, b) => a.order - b.order);
}
